// ===================================================================
// HMGP — Applicant Portal
// Org-scoped portal for HMGP applicant roles. When applicant roles
// access the HMGP module, they see this portal instead of admin view.
// ===================================================================

import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Textarea,
  Select, Modal, SimpleGrid, Table, ActionIcon, Loader, Center,
} from '../../../components/gab/GabComponents';
import {
  IconPlus, IconSend, IconArrowBack, IconChevronDown, IconChevronRight,
  IconFileText, IconBuildingBank, IconAlertCircle, IconCoin, IconReceipt,
  IconRefresh, IconExternalLink, IconEdit, IconUpload, IconChecklist,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { fmtDate } from '../../../lib/date-format';
import { useAuthStore } from '../../../store/authStore';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType, type HMGPSubapplication, type HMGPSubappStatus,
  type PAReimbursementRequest,
} from '../../../types/pagrants';
import { FIELDS, TABLES, createRecord, updateRecord } from '../../../lib/api-switch';
import { uploadFileToV2 } from '../../../lib/uploadFile';

// ── Helpers ──────────────────────────────────────────────────────

const GREEN = '#059669';

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function num(v: string | number): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

const SUBAPP_STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', under_review: '#C68700',
  prioritized: '#03AC9E', forwarded_to_fema: 'var(--color-success)', returned: '#F86A0B', withdrawn: 'var(--color-error)',
};

const PROJECT_TYPE_OPTIONS = Object.entries(HMGP_PROJECT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

const PHASE_COLORS: Record<string, string> = {
  subapplication: 'var(--color-primary-700)', review: '#C68700', awarded: 'var(--color-success)',
  in_progress: '#03AC9E', closeout: 'var(--data-viz-650)', denied: 'var(--color-error)',
};

const RFR_STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', under_review: '#C68700',
  approved: 'var(--color-success)', denied: 'var(--color-error)', revision_needed: '#F86A0B', paid: '#03AC9E',
};

const RFR_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  approved: 'Approved', denied: 'Denied', revision_needed: 'Revision Needed', paid: 'Paid',
};

// ── Component ────────────────────────────────────────────────────

export default function HMGPApplicantPortal() {
  const navigate = useNavigate();
  const activeDisaster = useActiveDisaster();
  const disasterId = activeDisaster ? String(activeDisaster.Rid) : '';
  const myOrg = useMyPAOrg();
  const user = useAuthStore(s => s.user);

  const {
    projects, subapplications, loading, loadingSubs,
    fetchProjects, fetchSubapplications,
    createSubapplication, updateSubapplication,
  } = useHMGPStore();

  const reimbursements = usePAGrantsStore(s => s.reimbursementRequests);
  const fetchReimbursements = usePAGrantsStore(s => s.fetchReimbursements);

  const paDocuments = usePAGrantsStore(s => s.documents);
  const fetchDocuments = usePAGrantsStore(s => s.fetchDocuments);

  // ── Initial fetch ──────────────────────────────────────────────
  useEffect(() => {
    fetchProjects();
    fetchSubapplications();
    fetchReimbursements();
    fetchDocuments();
  }, []);

  // ── Org-scoped data ────────────────────────────────────────────
  const orgId = myOrg ? String(myOrg.Rid) : '';

  const mySubapps = useMemo(() => {
    if (!disasterId || !orgId) return [];
    return subapplications.filter(
      s => s.disasterId === disasterId && String(s.organizationId) === orgId
    );
  }, [subapplications, disasterId, orgId]);

  const myProjects = useMemo(() => {
    if (!disasterId || !orgId) return [];
    return projects.filter(
      p => String(p.disasterId) === disasterId && String(p.organizationId) === orgId
    );
  }, [projects, disasterId, orgId]);

  const myProjectIds = useMemo(() => new Set(myProjects.map(p => String(p.Rid))), [myProjects]);

  const myReimbursements: PAReimbursementRequest[] = useMemo(() => {
    if (!disasterId || myProjectIds.size === 0) return [];
    return reimbursements.filter((r: PAReimbursementRequest) => myProjectIds.has(String(r.projectId)));
  }, [reimbursements, disasterId, myProjectIds]);

  // ── Stats ──────────────────────────────────────────────────────
  const pendingActions = mySubapps.filter(s => s.status === 'draft' || s.status === 'returned').length;
  const totalEstCost = myProjects.reduce((sum, p) => sum + num(p.estimatedCost), 0);

  // ── Create subapplication form ─────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    subappTitle: '', projectType: '', problemStatement: '', proposedActivity: '',
    estimatedCost: '', federalRequest: '', nonFederalMatch: '', projectAddress: '',
  });
  const [saving, setSaving] = useState(false);

  function resetForm() {
    setForm({
      subappTitle: '', projectType: '', problemStatement: '', proposedActivity: '',
      estimatedCost: '', federalRequest: '', nonFederalMatch: '', projectAddress: '',
    });
  }

  async function handleCreate() {
    if (!form.subappTitle.trim()) {
      notifications.show({ title: 'Required', message: 'Title is required', color: 'var(--color-error)' });
      return;
    }
    setSaving(true);
    try {
      await createSubapplication({
        ...form,
        organizationId: orgId,
        disasterId,
        creatorId: user?.email || '',
        status: 'draft',
      });
      notifications.show({ title: 'Created', message: 'Subapplication created as draft', color: 'var(--color-success)' });
      setShowCreate(false);
      resetForm();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create subapplication', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── Submit / Resubmit ──────────────────────────────────────────
  async function handleSubmit(sub: HMGPSubapplication) {
    await updateSubapplication(sub.Rid, { status: 'submitted' as any, submittedDate: new Date().toISOString().split('T')[0], submittedBy: user?.email || '' });
    notifications.show({ title: 'Submitted', message: `${sub.subappNumber || sub.subappTitle} submitted for review`, color: 'var(--color-primary-700)' });
  }

  // ── Edit subapplication ────────────────────────────────────────
  const [editingSub, setEditingSub] = useState<HMGPSubapplication | null>(null);

  function openEdit(sub: HMGPSubapplication) {
    setForm({
      subappTitle: sub.subappTitle || '',
      projectType: sub.projectType || '',
      problemStatement: sub.problemStatement || '',
      proposedActivity: sub.proposedActivity || '',
      estimatedCost: sub.estimatedCost || '',
      federalRequest: sub.federalRequest || '',
      nonFederalMatch: sub.nonFederalMatch || '',
      projectAddress: sub.projectAddress || '',
    });
    setEditingSub(sub);
    setShowCreate(true);
  }

  async function handleSaveEdit() {
    if (!editingSub) return;
    if (!form.subappTitle.trim()) {
      notifications.show({ title: 'Required', message: 'Title is required', color: 'var(--color-error)' });
      return;
    }
    setSaving(true);
    try {
      await updateSubapplication(editingSub.Rid, {
        subappTitle: form.subappTitle,
        projectType: form.projectType as any,
        problemStatement: form.problemStatement,
        proposedActivity: form.proposedActivity,
        estimatedCost: form.estimatedCost,
        federalRequest: form.federalRequest,
        nonFederalMatch: form.nonFederalMatch,
        projectAddress: form.projectAddress,
      });
      notifications.show({ title: 'Saved', message: 'Subapplication updated', color: 'var(--color-success)' });
      setShowCreate(false);
      setEditingSub(null);
      resetForm();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update subapplication', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── Document upload ───────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProjectId, setUploadProjectId] = useState<string>('');

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !uploadProjectId) return;
    try {
      const FDoc = FIELDS.paDocuments;
      const fields: Array<[string, string]> = [
        [FDoc.projectId, uploadProjectId],
        [FDoc.organizationId, orgId],
        [FDoc.title, file.name],
        [FDoc.documentType, 'supporting'],
        [FDoc.uploadedBy, user?.email || ''],
        [FDoc.notes, `Uploaded by ${user?.email || 'applicant'} from HMGP portal`],
      ];
      const rid = await createRecord(TABLES.paDocuments, fields);
      const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.paDocuments);
      const notesWithDocId = `Uploaded by ${user?.email || 'applicant'} from HMGP portal|||DOCID|||${documentId}|||ATTACHID|||${attachmentId}|||`;
      await updateRecord(TABLES.paDocuments, rid, [
        [FDoc.file, attachmentId],
        [FDoc.notes, notesWithDocId],
      ]);
      notifications.show({ title: 'Uploaded', message: `${file.name} attached to project`, color: 'var(--color-success)' });
      fetchDocuments();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to upload document', color: 'var(--color-error)' });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── My documents count ────────────────────────────────────────
  const myDocCount = useMemo(() => {
    if (!myProjectIds.size) return 0;
    return paDocuments.filter(d => myProjectIds.has(String(d.projectId))).length;
  }, [paDocuments, myProjectIds]);

  // ── Expandable row state ──────────────────────────────────────
  const [expandedSub, setExpandedSub] = useState<number | null>(null);

  // ══════════════════════════════════════════════════════════════
  // No Active Disaster
  // ══════════════════════════════════════════════════════════════
  if (!activeDisaster) {
    return (
      <Stack gap="md" p="md">
        <Title order={3}>HMGP Applicant Portal</Title>
        <Paper p="xl" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <IconAlertCircle size={40} color="#6B7280" style={{ marginBottom: 8 }} />
          <Text size="lg" fw={600} c="dimmed" mb="sm">No Active Disaster</Text>
          <Text size="sm" c="dimmed">Select an incident from the top navigation to view your HMGP portal.</Text>
          <Button mt="md" variant="light" onClick={() => navigate('/')}>Go to Incident Hub</Button>
        </Paper>
      </Stack>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // No Org Found (Onboarding)
  // ══════════════════════════════════════════════════════════════
  if (!myOrg) {
    return (
      <Stack gap="md" p="md">
        <Title order={3}>HMGP Applicant Portal</Title>
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <IconBuildingBank size={48} color="#6B7280" style={{ marginBottom: 16 }} />
          <Title order={4} c="dimmed" mb="xs">Organization Not Found</Title>
          <Text size="sm" c="dimmed" mb="md">
            No organization is linked to your account ({user?.email || 'unknown'}).
          </Text>
          <Text size="sm" c="dimmed">
            Contact your grantee administrator to add your email to your organization's authorized contacts.
          </Text>
          <Button mt="lg" variant="light" onClick={() => navigate('/')}>Return Home</Button>
        </Paper>
      </Stack>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // Loading
  // ══════════════════════════════════════════════════════════════
  if (loading || loadingSubs) {
    return (
      <Center h={400}>
        <Loader color={GREEN} size="lg" />
      </Center>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // Main Portal
  // ══════════════════════════════════════════════════════════════
  return (
    <Stack gap="md" p="md">
      {/* ── Header ────────────────────────────────────────────── */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>HMGP Portal — {myOrg.orgName}</Title>
          <Text size="sm" c="dimmed">{activeDisaster.disasterName} ({activeDisaster.disasterNumber})</Text>
        </div>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          onClick={() => { fetchProjects(); fetchSubapplications(); fetchReimbursements(); }}>
          Refresh
        </Button>
      </Group>

      {/* ── Dashboard Cards ───────────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${GREEN}` }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>My Subapplications</Text>
          <Text size="xl" fw={700} c={GREEN}>{mySubapps.length}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>My Projects</Text>
          <Text size="xl" fw={700} c="#0284C7">{myProjects.length}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${pendingActions > 0 ? '#D97706' : '#9CA3AF'}` }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Pending Actions</Text>
          <Text size="xl" fw={700} c={pendingActions > 0 ? '#D97706' : '#9CA3AF'}>{pendingActions}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Est. Cost</Text>
          <Text size="xl" fw={700} c="#7C3AED">{fmt(totalEstCost)}</Text>
        </Paper>
      </SimpleGrid>

      {/* ── Subapplications ───────────────────────────────────── */}
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconFileText size={20} color={GREEN} />
            <Title order={4}>My Subapplications</Title>
          </Group>
          <Button size="xs" color="var(--color-success)" leftSection={<IconPlus size={14} />}
            onClick={() => { resetForm(); setEditingSub(null); setShowCreate(true); }}>
            New Subapplication
          </Button>
        </Group>

        {mySubapps.length === 0 ? (
          <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
            <Text c="dimmed" size="sm">No subapplications yet. Create one to start the HMGP process.</Text>
          </Paper>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th w={30}></Table.Th>
                <Table.Th>SA #</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>BCA Ratio</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th w={120}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mySubapps.map(sub => {
                const isExpanded = expandedSub === sub.Rid;
                const typeColor = HMGP_PROJECT_TYPE_COLORS[sub.projectType as HMGPProjectType] || '#6B7280';
                return (
                  <>
                    <Table.Tr key={sub.Rid} style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedSub(isExpanded ? null : sub.Rid)}>
                      <Table.Td>
                        <ActionIcon variant="subtle" size="xs" color="#939598">
                          {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                        </ActionIcon>
                      </Table.Td>
                      <Table.Td><Text size="sm" fw={500}>{sub.subappNumber || '---'}</Text></Table.Td>
                      <Table.Td><Text size="sm">{sub.subappTitle || '---'}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="sm" style={{ backgroundColor: typeColor, color: '#fff' }}>
                          {HMGP_PROJECT_TYPE_LABELS[sub.projectType as HMGPProjectType] || sub.projectType || '---'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {sub.bcaRatio ? (
                          <Badge size="sm" color={parseFloat(sub.bcaRatio) >= 1 ? 'var(--color-success)' : 'var(--color-error)'}>
                            {parseFloat(sub.bcaRatio).toFixed(2)}
                          </Badge>
                        ) : <Text size="sm" c="dimmed">---</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={SUBAPP_STATUS_COLORS[sub.status] || '#939598'}>
                          {HMGP_SUBAPP_STATUS_LABELS[sub.status as HMGPSubappStatus] || sub.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(sub.createdDate)}</Text></Table.Td>
                      <Table.Td onClick={e => e.stopPropagation()}>
                        <Group gap={4}>
                          {(sub.status === 'draft' || sub.status === 'returned') && (
                            <ActionIcon size="sm" variant="light" color="#939598"
                              title="Edit subapplication"
                              onClick={() => openEdit(sub)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                          )}
                          {sub.status === 'draft' && (
                            <Button size="compact-xs" color="var(--color-primary-700)" variant="light"
                              leftSection={<IconSend size={12} />}
                              onClick={() => handleSubmit(sub)}>
                              Submit
                            </Button>
                          )}
                          {sub.status === 'returned' && (
                            <Button size="compact-xs" color="#F86A0B" variant="light"
                              leftSection={<IconArrowBack size={12} />}
                              onClick={() => handleSubmit(sub)}>
                              Resubmit
                            </Button>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    {isExpanded && (
                      <Table.Tr key={`${sub.Rid}-detail`}>
                        <Table.Td colSpan={8} style={{ background: '#f9fafb' }}>
                          <Stack gap="xs" p="sm">
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Problem Statement</Text>
                                <Text size="sm">{sub.problemStatement || '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Proposed Activity</Text>
                                <Text size="sm">{sub.proposedActivity || '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Estimated Cost</Text>
                                <Text size="sm">{sub.estimatedCost ? fmt(num(sub.estimatedCost)) : '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Federal Request</Text>
                                <Text size="sm">{sub.federalRequest ? fmt(num(sub.federalRequest)) : '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Non-Federal Match</Text>
                                <Text size="sm">{sub.nonFederalMatch ? fmt(num(sub.nonFederalMatch)) : '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Match Source</Text>
                                <Text size="sm">{sub.matchSource || '---'}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>Location</Text>
                                <Text size="sm">
                                  {[sub.projectAddress, sub.projectCity, sub.projectCounty, sub.projectState].filter(Boolean).join(', ') || '---'}
                                </Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" fw={600}>BCA Status</Text>
                                <Text size="sm">{sub.bcaStatus?.replace(/_/g, ' ') || '---'}</Text>
                              </div>
                              {sub.reviewNotes && (
                                <div>
                                  <Text size="xs" c="dimmed" fw={600}>Reviewer Notes</Text>
                                  <Text size="sm" c="#F86A0B">{sub.reviewNotes}</Text>
                                </div>
                              )}
                              {sub.linkedProjectId && (
                                <div>
                                  <Text size="xs" c="dimmed" fw={600}>Linked Project</Text>
                                  <Button size="compact-xs" variant="light" color="var(--color-success)"
                                    leftSection={<IconExternalLink size={12} />}
                                    onClick={() => navigate(`/pagrants/hmgp/projects/${sub.linkedProjectId}`)}>
                                    View Project
                                  </Button>
                                </div>
                              )}
                            </SimpleGrid>
                            <div>
                              <Text size="xs" c="dimmed" fw={600}>Notes</Text>
                              <Text size="sm">{sub.notes || '---'}</Text>
                            </div>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </>
                );
              })}
            </Table.Tbody>
          </Table>
          </div>
        )}
      </Paper>

      {/* ── Action Items ─────────────────────────────────────── */}
      {(mySubapps.some(s => s.status === 'draft' || s.status === 'returned') || myProjects.length > 0) && (
        <Paper withBorder radius="md" p="md" style={{ borderLeft: '4px solid #D97706' }}>
          <Group gap="xs" mb="sm">
            <IconChecklist size={20} color="#D97706" />
            <Title order={4}>Action Items</Title>
          </Group>
          <Stack gap="xs">
            {mySubapps.filter(s => s.status === 'draft').map(s => (
              <Group key={s.Rid} gap="xs" justify="space-between" p="xs" style={{ background: '#f9fafb', borderRadius: 4 }}>
                <div>
                  <Text size="sm" fw={500}>{s.subappTitle || s.subappNumber || '---'}</Text>
                  <Text size="xs" c="dimmed">Draft subapplication — complete and submit for review</Text>
                </div>
                <Group gap={4}>
                  <Button size="compact-xs" variant="light" color="#939598" leftSection={<IconEdit size={12} />}
                    onClick={() => openEdit(s)}>Edit</Button>
                  <Button size="compact-xs" variant="light" color="var(--color-primary-700)" leftSection={<IconSend size={12} />}
                    onClick={() => handleSubmit(s)}>Submit</Button>
                </Group>
              </Group>
            ))}
            {mySubapps.filter(s => s.status === 'returned').map(s => (
              <Group key={s.Rid} gap="xs" justify="space-between" p="xs" style={{ background: '#fff7ed', borderRadius: 4 }}>
                <div>
                  <Text size="sm" fw={500}>{s.subappTitle || s.subappNumber || '---'}</Text>
                  <Text size="xs" c="#F86A0B">Returned — revise and resubmit</Text>
                  {s.reviewNotes && <Text size="xs" c="dimmed" mt={2}>Feedback: {s.reviewNotes}</Text>}
                </div>
                <Group gap={4}>
                  <Button size="compact-xs" variant="light" color="#939598" leftSection={<IconEdit size={12} />}
                    onClick={() => openEdit(s)}>Edit</Button>
                  <Button size="compact-xs" variant="light" color="#F86A0B" leftSection={<IconArrowBack size={12} />}
                    onClick={() => handleSubmit(s)}>Resubmit</Button>
                </Group>
              </Group>
            ))}
            {myProjects.filter(p => {
              const docCount = paDocuments.filter(d => String(d.projectId) === String(p.Rid)).length;
              return docCount === 0;
            }).map(p => (
              <Group key={p.Rid} gap="xs" justify="space-between" p="xs" style={{ background: '#f0f9ff', borderRadius: 4 }}>
                <div>
                  <Text size="sm" fw={500}>{p.projectTitle || p.projectNumber || '---'}</Text>
                  <Text size="xs" c="dimmed">No documents uploaded — attach required supporting files</Text>
                </div>
                <Button size="compact-xs" variant="light" color="var(--color-primary-700)" leftSection={<IconUpload size={12} />}
                  onClick={() => { setUploadProjectId(String(p.Rid)); fileInputRef.current?.click(); }}>
                  Upload
                </Button>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}

      {/* ── My Projects ───────────────────────────────────────── */}
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconCoin size={20} color="#0284C7" />
            <Title order={4}>My Projects</Title>
            {myDocCount > 0 && <Badge size="sm" variant="light" color="var(--color-primary-700)">{myDocCount} docs</Badge>}
          </Group>
        </Group>
        <input ref={fileInputRef} type="file" hidden
          accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.zip"
          onChange={handleFileUpload} />

        {myProjects.length === 0 ? (
          <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
            <Text c="dimmed" size="sm">No HMGP projects yet. Projects are created when subapplications are approved and promoted.</Text>
          </Paper>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th>Project #</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Phase</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Award Amount</Table.Th>
                <Table.Th w={80}>Docs</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {myProjects.map(p => {
                const typeColor = HMGP_PROJECT_TYPE_COLORS[p.projectType as HMGPProjectType] || '#6B7280';
                const docCount = paDocuments.filter(d => String(d.projectId) === String(p.Rid)).length;
                return (
                  <Table.Tr key={p.Rid} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/pagrants/hmgp/projects/${p.Rid}`)}>
                    <Table.Td><Text size="sm" fw={500}>{p.projectNumber || '---'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{p.projectTitle || '---'}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="sm" style={{ backgroundColor: typeColor, color: '#fff' }}>
                        {HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType || '---'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={PHASE_COLORS[p.phase] || '#939598'}>
                        {p.phase?.replace(/_/g, ' ') || '---'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="sm">{p.status || '---'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {p.awardAmount ? fmt(num(p.awardAmount)) : '---'}
                      </Text>
                    </Table.Td>
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Group gap={4}>
                        <Badge size="xs" variant="light" color={docCount > 0 ? 'var(--color-primary-700)' : '#939598'}>{docCount}</Badge>
                        <ActionIcon size="sm" variant="light" color="var(--color-primary-700)" title="Upload document"
                          onClick={() => { setUploadProjectId(String(p.Rid)); fileInputRef.current?.click(); }}>
                          <IconUpload size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </div>
        )}
      </Paper>

      {/* ── My Reimbursements ─────────────────────────────────── */}
      <Paper withBorder radius="md" p="md">
        <Group gap="xs" mb="sm">
          <IconReceipt size={20} color="#7C3AED" />
          <Title order={4}>My Reimbursements</Title>
        </Group>

        {myReimbursements.length === 0 ? (
          <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
            <Text c="dimmed" size="sm">No reimbursement requests linked to your HMGP projects.</Text>
          </Paper>
        ) : (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs" mb="sm">
              <Paper p="xs" withBorder ta="center">
                <Text size="xs" c="dimmed">Total RFRs</Text>
                <Text fw={700}>{myReimbursements.length}</Text>
              </Paper>
              <Paper p="xs" withBorder ta="center">
                <Text size="xs" c="dimmed">Requested</Text>
                <Text fw={700} c="var(--color-primary-700)">
                  {fmt(myReimbursements.reduce((s, r) => s + num(r.requestedAmount), 0))}
                </Text>
              </Paper>
              <Paper p="xs" withBorder ta="center">
                <Text size="xs" c="dimmed">Approved</Text>
                <Text fw={700} c="var(--color-success)">
                  {fmt(myReimbursements.filter(r => r.status === 'approved' || r.status === 'paid').reduce((s, r) => s + num(r.approvedAmount || r.requestedAmount), 0))}
                </Text>
              </Paper>
              <Paper p="xs" withBorder ta="center">
                <Text size="xs" c="dimmed">Paid</Text>
                <Text fw={700} c="#03AC9E">
                  {fmt(myReimbursements.filter(r => r.status === 'paid').reduce((s, r) => s + num(r.approvedAmount || r.requestedAmount), 0))}
                </Text>
              </Paper>
            </SimpleGrid>

            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>RFR #</Table.Th>
                  <Table.Th>Project</Table.Th>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {myReimbursements.map(r => {
                  const proj = myProjects.find(p => String(p.Rid) === String(r.projectId));
                  return (
                    <Table.Tr key={r.Rid}>
                      <Table.Td><Text size="sm" fw={500}>{r.requestNumber || `RFR-${r.Rid}`}</Text></Table.Td>
                      <Table.Td><Text size="sm">{proj?.projectTitle || '---'}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmt(num(r.requestedAmount))}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={RFR_STATUS_COLORS[r.status] || '#939598'}>
                          {RFR_STATUS_LABELS[r.status] || r.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(r.submittedDate)}</Text></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            </div>
          </>
        )}
      </Paper>

      {/* ── Create Subapplication Modal ───────────────────────── */}
      <Modal opened={showCreate} onClose={() => { setShowCreate(false); setEditingSub(null); resetForm(); }}
        title={editingSub ? `Edit Subapplication — ${editingSub.subappTitle}` : 'New Subapplication'} size="lg">
        <Stack gap="sm">
          <TextInput label="Title" required placeholder="Mitigation proposal title"
            value={form.subappTitle} onChange={e => setForm(f => ({ ...f, subappTitle: e.target.value }))} />
          <Select label="Mitigation Type" required placeholder="Select type"
            data={PROJECT_TYPE_OPTIONS}
            value={form.projectType} onChange={v => setForm(f => ({ ...f, projectType: v || '' }))} />
          <Textarea label="Problem Statement" placeholder="Describe the hazard and its impact"
            minRows={3} value={form.problemStatement}
            onChange={e => setForm(f => ({ ...f, problemStatement: e.target.value }))} />
          <Textarea label="Proposed Activity" placeholder="Describe the mitigation measure"
            minRows={3} value={form.proposedActivity}
            onChange={e => setForm(f => ({ ...f, proposedActivity: e.target.value }))} />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput label="Estimated Cost" placeholder="$0"
              value={form.estimatedCost} onChange={e => setForm(f => ({ ...f, estimatedCost: e.target.value }))} />
            <TextInput label="Federal Request (75%)" placeholder="$0"
              value={form.federalRequest} onChange={e => setForm(f => ({ ...f, federalRequest: e.target.value }))} />
            <TextInput label="Non-Federal Match (25%)" placeholder="$0"
              value={form.nonFederalMatch} onChange={e => setForm(f => ({ ...f, nonFederalMatch: e.target.value }))} />
          </SimpleGrid>
          <TextInput label="Project Address" placeholder="123 Main St, City, State"
            value={form.projectAddress} onChange={e => setForm(f => ({ ...f, projectAddress: e.target.value }))} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => { setShowCreate(false); setEditingSub(null); resetForm(); }}>Cancel</Button>
            <Button color="var(--color-success)" loading={saving} onClick={editingSub ? handleSaveEdit : handleCreate}>
              {editingSub ? 'Save Changes' : 'Create Draft'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
